import { useMemo, useState } from "react"
import { router } from "@inertiajs/react"
import ProductCard from "@/components/storefront/ProductCard"
import FilterPanel from "@/components/storefront/FilterPanel"
import Breadcrumb from "@/components/storefront/Breadcrumb"
import type {
  StorefrontItem,
  Category,
  SortOption,
  FilterState,
  CatalogLabels,
} from "@/components/storefront/types"

interface StoreCatalogProps {
  brandName: string
  labels: CatalogLabels
  urls: { root: string; store: string }
  categories: Category[]
  items: StorefrontItem[]
  filters: FilterState
  sortOptions: SortOption[]
  i18n: {
    soldout: string
    last_pieces: string
    no_image: string
    pre_sale: string
  }
  showPriceFilter?: boolean
  libraryUrl?: string | null
}

export default function StoreCatalog({
  brandName,
  labels,
  urls,
  categories,
  items,
  filters,
  sortOptions,
  i18n,
  showPriceFilter = true,
  libraryUrl,
}: StoreCatalogProps) {
  const [panelOpen, setPanelOpen] = useState(false)

  const selectedCategories = useMemo(
    () => new Set(filters.selected_categories),
    [filters.selected_categories]
  )

  const buildFilterParams = (overrides: Partial<{
    query: string
    sort: string
    minPrice: number
    maxPrice: number
    categories: string[]
  }>) => {
    const merged = {
      query: overrides.query ?? filters.query,
      sort: overrides.sort ?? filters.sort,
      minPrice: overrides.minPrice ?? filters.min_price,
      maxPrice: overrides.maxPrice ?? filters.max_price,
      categories: overrides.categories ?? filters.selected_categories,
    }

    const filtersParam: Record<string, string | number> = {
      query: merged.query,
      sort: merged.sort,
      min_price: merged.minPrice,
      max_price: merged.maxPrice,
    }

    categories.forEach((category) => {
      filtersParam[category.name] = merged.categories.includes(category.name) ? 1 : 0
    })

    return { filters: filtersParam }
  }

  const visitWithFilters = (overrides: Parameters<typeof buildFilterParams>[0]) => {
    router.get(urls.store, buildFilterParams(overrides), {
      preserveScroll: true,
      preserveState: true,
    })
  }

  const toggleCategory = (categoryName: string) => {
    const next = new Set(selectedCategories)
    if (next.has(categoryName)) {
      next.delete(categoryName)
    } else {
      next.add(categoryName)
    }
    visitWithFilters({ categories: Array.from(next) })
  }

  return (
    <>
      <section className="bg-light pt-24 pb-8">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Breadcrumb
            items={[
              { label: brandName, href: urls.root },
              { label: labels.title },
            ]}
            className="mb-4"
          />
          <div className="flex items-center justify-between mb-8">
            <a
              href={urls.root}
              className="inline-flex items-center border border-contrast rounded-md px-4 py-2 text-sm font-medium text-contrast hover:bg-contrast hover:text-light transition-colors"
            >
              {labels.return}
            </a>

            <h1 className="text-2xl md:text-3xl font-bold text-contrast">
              {labels.title}
            </h1>

            <div className="flex items-center gap-3">
              {libraryUrl && (
                <a
                  href={libraryUrl}
                  className="inline-flex items-center bg-contrast text-light rounded-md px-4 py-2 text-sm font-medium hover:bg-black transition-colors"
                >
                  Bibliothèque
                </a>
              )}
              <button
                type="button"
                onClick={() => setPanelOpen(true)}
                className="inline-flex items-center bg-contrast text-light rounded-md px-4 py-2 text-sm font-medium hover:bg-black transition-colors cursor-pointer"
              >
                {labels.filter}
              </button>
            </div>
          </div>

          {categories.length > 0 && (
            <div className="flex flex-wrap gap-3">
              {categories.map((category) => {
                const active = selectedCategories.has(category.name)
                return (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => toggleCategory(category.name)}
                    aria-pressed={active}
                    className={`px-4 py-2 text-sm font-medium rounded-full border transition-colors cursor-pointer ${
                      active
                        ? "border-contrast text-light bg-contrast hover:bg-black"
                        : "border-gray-300 text-gray-400 line-through hover:border-contrast hover:text-contrast hover:no-underline"
                    }`}
                  >
                    {category.name}
                  </button>
                )
              })}
            </div>
          )}
        </div>
      </section>

      <section className="bg-light pb-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {items.length === 0 ? (
            <div className="flex items-center justify-center py-24">
              <h2 className="text-2xl text-midgray">{labels.no_products}</h2>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {items.map((item) => (
                <ProductCard key={item.id} item={item} i18n={i18n} />
              ))}
            </div>
          )}
        </div>
      </section>

      <FilterPanel
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        showPrice={showPriceFilter}
        labels={{
          search_placeholder: labels.search_placeholder,
          sort_label: labels.sort_label,
          price_label: labels.price_label,
          min_price: labels.min_price,
          max_price: labels.max_price,
          apply: labels.apply,
          reset: labels.reset,
        }}
        initial={{
          query: filters.query,
          sort: filters.sort,
          minPrice: filters.min_price,
          maxPrice: filters.max_price,
          availableMaxPrice: filters.available_max_price,
        }}
        sortOptions={sortOptions}
        onApply={(applied) => {
          setPanelOpen(false)
          visitWithFilters(applied)
        }}
        onReset={() => {
          setPanelOpen(false)
          router.get(
            urls.store,
            {},
            { preserveScroll: true, preserveState: false }
          )
        }}
      />
    </>
  )
}
